<template>
  <div class="opv" data-testid="order-photos-viewer">
    <!-- The order's photos are still on their way (the order open in the camera is fetched). -->
    <div v-if="status === 'loading'" class="bz-empty opv-state" role="status" aria-live="polite" data-testid="order-photos-loading">
      <BzIcon name="progress_activity" class="bz-empty__icon bz-spinner" />
      <p class="bz-empty__title">{{ $t('message.orderPhotosLoading') }}</p>
    </div>

    <!-- Could not fetch them. Never a dead end: retry, or the way back the header already offers. -->
    <div v-else-if="status === 'failed' || status === 'offline'" class="bz-empty opv-state" data-testid="order-photos-failed">
      <BzIcon :name="status === 'offline' ? 'wifi_off' : 'warning'" class="bz-empty__icon" />
      <p class="bz-empty__title">{{ $t(status === 'offline' ? 'message.orderPhotosOffline' : 'message.orderPhotosLoadFailed') }}</p>
      <p v-if="status === 'offline'" class="bz-empty__text">{{ $t('message.guidedFlowUnavailableOffline') }}</p>
      <div class="opv-state-actions">
        <BzButton
          kind="primary"
          size="lg"
          icon="refresh"
          :label="$t('message.guidedFlowRetry')"
          data-testid="order-photos-retry"
          @click="$emit('retry')"
        />
      </div>
    </div>

    <!-- Nothing on the server yet: usually the photo is still uploading from this very tablet. -->
    <div v-else-if="!photos.length" class="bz-empty opv-state" data-testid="order-photos-none">
      <BzIcon name="image" class="bz-empty__icon" />
      <p class="bz-empty__title">{{ $t('message.orderPhotosNone') }}</p>
      <p class="bz-empty__text">{{ $t('message.orderPhotosNoneHint') }}</p>
      <div class="opv-state-actions">
        <!-- "They take a few seconds" has to come with a way to look again without starting over. -->
        <BzButton
          kind="primary"
          size="lg"
          icon="refresh"
          :label="$t('message.orderPhotosCheckAgain')"
          data-testid="order-photos-check-again"
          @click="$emit('retry')"
        />
        <BzButton
          kind="secondary"
          size="lg"
          icon="list"
          :label="$t('message.orderPhotosOtherOrders')"
          data-testid="order-photos-browse"
          @click="$emit('browse')"
        />
      </div>
    </div>

    <!-- The photo, as big as the screen allows. Swipe or the two side buttons move between them. -->
    <div
      v-else
      class="opv-stage"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointercancel="swipe = null"
    >
      <img
        :key="src"
        :src="src"
        :alt="$t('message.orderPhotosAlt', { index: index + 1, code })"
        class="opv-img"
        :class="{ 'is-hidden': state !== 'loaded' }"
        decoding="async"
        draggable="false"
        data-testid="order-photos-image"
        @load="onLoad($event)"
        @error="onError($event)"
      />

      <div v-if="state === 'loading'" class="bz-empty opv-overlay" role="status" aria-live="polite">
        <BzIcon name="progress_activity" class="bz-empty__icon bz-spinner" />
        <p class="bz-empty__title">{{ $t('message.orderPhotosLoading') }}</p>
      </div>
      <div v-else-if="state === 'failed'" class="bz-empty opv-overlay" data-testid="order-photos-image-failed">
        <BzIcon name="warning" class="bz-empty__icon" />
        <p class="bz-empty__title">{{ $t('message.orderPhotosImageFailed') }}</p>
        <BzButton
          kind="primary"
          size="lg"
          icon="refresh"
          :label="$t('message.guidedFlowRetry')"
          data-testid="order-photos-image-retry"
          @click="retryImage"
        />
      </div>

      <span v-if="counterText" class="bz-chip opv-counter" data-testid="order-photos-counter"><span class="opv-counter-text">{{ counterText }}</span></span>

      <BzButton
        v-if="many && index > 0"
        class="opv-nav opv-nav--prev"
        kind="secondary"
        size="lg"
        icon="chevron_left"
        :aria-label="$t('message.orderPhotosPrev')"
        data-testid="order-photos-prev"
        @click="go(index - 1)"
      />
      <BzButton
        v-if="many && index < photos.length - 1"
        class="opv-nav opv-nav--next"
        kind="secondary"
        size="lg"
        icon="chevron_right"
        :aria-label="$t('message.orderPhotosNext')"
        data-testid="order-photos-next"
        @click="go(index + 1)"
      />
    </div>
  </div>
</template>

<script>
/**
 * OrderPhotosViewer — one order's photos, one at a time, as large as the help modal allows.
 *
 * Presentational: it gets the photos (`{ url, timeLabel }`, oldest first) and a fetch status,
 * and owns only what is purely visual — which photo is on screen and whether it has loaded.
 *
 * Why one photo at a time and not a strip of thumbnails: an order has one photo at the median and
 * three at p90 (7 days of production, sep-2026), each a full-resolution JPEG from a public S3 URL.
 * A strip would download and decode every one of them just to draw it small, on the €100 tablet
 * and the kitchen's WiFi, while the camera keeps running behind the modal. Instead the photo on
 * screen loads first and only the NEXT one is warmed up in the background.
 *
 * Why buttons AND swipe: a swipe is what a phone taught everyone, but a gloved or greasy finger
 * does not always drag cleanly, and a 72 px target at the edge of the photo always works.
 */
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';

const SWIPE_MIN_PX = 48;

export default {
  name: 'OrderPhotosViewer',
  components: { BzButton, BzIcon },
  props: {
    /** `[{ url, timeLabel }]`, in the order they were taken. */
    photos: { type: Array, default: () => [] },
    /** 'loading' | 'ready' | 'failed' | 'offline' — the fetch of the order's photos. */
    status: { type: String, default: 'ready' },
    /** The order code, for the alt text. */
    code: { type: String, default: '' },
  },
  emits: ['retry', 'browse', 'photo-load'],
  data() {
    return {
      index: 0,
      settled: {},  // url → 'loaded' | 'failed'
      attempts: {}, // url → retries so far (busts the failed request)
      swipe: null,  // { x, y } at pointerdown
    };
  },
  computed: {
    many() {
      return this.photos.length > 1;
    },
    // "Photo 2 of 3 · 4 min ago": what the chip over the photo says; nothing, if there is nothing to say.
    counterText() {
      const parts = [];
      if (this.many) parts.push(this.$t('message.orderPhotosCounter', { index: this.index + 1, count: this.photos.length }));
      if (this.current.timeLabel) parts.push(this.current.timeLabel);
      return parts.join(' · ');
    },
    current() {
      return this.photos[this.index] || this.photos[0] || { url: '', timeLabel: '' };
    },
    src() {
      const url = this.current.url;
      const attempt = this.attempts[url] || 0;
      if (!url || !attempt) return url;
      return `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`;
    },
    state() {
      return this.settled[this.current.url] === 'failed'
        ? 'failed'
        : this.settled[this.current.url] === 'loaded' ? 'loaded' : 'loading';
    },
  },
  watch: {
    // Another order: start from its first photo, and forget the load states of the previous one.
    photos(next, prev) {
      const same = prev && next.length === prev.length && next.every((p, i) => p.url === prev[i].url);
      if (same) return;
      this.index = 0;
      this.settled = {};
      this.attempts = {};
      this.reported = new Set();
      this.markShown();
    },
  },
  created() {
    this.preloads = [];   // not reactive: just keeps the warm-up Image objects alive
    this.shownAt = 0;
    this.reported = new Set();
    this.markShown();
  },
  beforeUnmount() {
    this.preloads = [];
  },
  methods: {
    now() {
      return typeof performance !== 'undefined' ? performance.now() : Date.now();
    },
    markShown() {
      this.shownAt = this.now();
    },
    go(next) {
      if (next < 0 || next >= this.photos.length || next === this.index) return;
      this.index = next;
      this.markShown();
    },
    // Only the <img> on screen may settle the photo on screen. With `:key="src"` the previous
    // element is detached but keeps downloading on a slow network, and its late load or error
    // would otherwise be written to the photo that replaced it (a spinner never shown, or a
    // "could not load" over a photo that loaded fine — and a false time in the telemetry).
    isCurrent(event) {
      return !event || !event.target || event.target.getAttribute('src') === this.src;
    },
    onLoad(event) {
      if (!this.isCurrent(event)) return;
      const url = this.current.url;
      this.settled = { ...this.settled, [url]: 'loaded' };
      this.report(url, true);
      this.warmNext();
    },
    onError(event) {
      if (!this.isCurrent(event)) return;
      const url = this.current.url;
      this.settled = { ...this.settled, [url]: 'failed' };
      this.report(url, false);
    },
    retryImage() {
      const url = this.current.url;
      const settled = { ...this.settled };
      delete settled[url];
      this.settled = settled;
      this.attempts = { ...this.attempts, [url]: (this.attempts[url] || 0) + 1 };
      this.markShown();
    },
    // Once per photo (and once more per retry): how long the kitchen waited for it, and whether
    // it came at all.
    report(url, ok) {
      const key = `${url}#${this.attempts[url] || 0}`;
      if (this.reported.has(key)) return;
      this.reported.add(key);
      this.$emit('photo-load', {
        index: this.index,
        ok,
        ms: Math.round(this.now() - this.shownAt),
        retried: Boolean(this.attempts[url]),
      });
    },
    warmNext() {
      const next = this.photos[this.index + 1];
      if (!next || this.settled[next.url] || typeof Image === 'undefined') return;
      const img = new Image();
      img.decoding = 'async';
      img.src = next.url;
      // One at a time: the HTTP cache keeps the bytes, and holding every decoded sensor-size JPEG
      // of a long order is memory the €100 tablet needs for the camera behind the modal.
      this.preloads = [img];
    },
    onPointerDown(event) {
      if (!this.many) return;
      this.swipe = { x: event.clientX, y: event.clientY };
    },
    onPointerUp(event) {
      const start = this.swipe;
      this.swipe = null;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      // Horizontal and decided: a short or diagonal drag is a tap that wobbled, not a swipe.
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.go(dx < 0 ? this.index + 1 : this.index - 1);
    },
  },
};
</script>

<style scoped>
.opv {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* The photo stage: the surface, flat, with the hairline edge, so the letterbox around a photo of
   another aspect ratio reads as a frame, not as missing content. `touch-action: pan-y` hands
   horizontal drags to the swipe and keeps vertical ones for the browser. */
.opv-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 220px;
  box-sizing: border-box;
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  overflow: hidden;
  background: var(--bz-surface);
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
}

/* Absolute and inset instead of max-height: 100%: a percentage max-height inside a flex item
   does not resolve, and the photo would overflow the screen with nothing to scroll it. */
.opv-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  -webkit-user-drag: none;
}

.opv-img.is-hidden { visibility: hidden; }

/* What the photo says when it has not come (loading, failed): the system's empty state, over the stage. */
.opv-overlay {
  position: absolute;
  inset: 0;
}

/* Over the photo: the system's tag, in the control grey — a flat, opaque plate holds its contrast on
   anything the camera saw. Read at arm's length, so a step above the tag's own size. */
.opv-counter {
  position: absolute;
  top: var(--bz-space-3);
  left: var(--bz-space-3);
  max-width: calc(100% - var(--bz-space-6));
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.opv-counter-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 72, round: the primary-target floor of the design norms, with a glove, at arm's length. BzButton's
   icon button is 48; the stage's two are the big ones. */
.opv-nav.bz-button {
  position: absolute;
  top: 50%;
  width: var(--bz-target-primary);
  height: var(--bz-target-primary);
  margin-top: calc(var(--bz-target-primary) / -2);
  touch-action: manipulation;
}

.opv-nav.bz-button :deep(.p-button-icon) { font-size: var(--bz-icon-lg); }

.opv-nav--prev { left: var(--bz-space-3); }
.opv-nav--next { right: var(--bz-space-3); }

/* ---- the whole-stage states: loading, failed, none ---- */
.opv-state {
  flex: 1 1 auto;
  min-height: 220px;
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

.opv-state-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bz-space-3) var(--bz-space-4);
  margin-top: var(--bz-space-4);
}

.opv-overlay .bz-button {
  margin-top: var(--bz-space-4);
}
</style>
